/**
 * Modelo de leitura do relatório de live (template v2, página contínua).
 *
 * Transforma o `byLive[]` do pipeline (atribuição + relatório de audiência +
 * camadas de receita + carrinho do público) no que cada seção imprime: números
 * já formatados, rótulos, tooltips e os textos de leitura. O HTML só
 * renderiza; toda regra de dado, de copy e de disponibilidade mora aqui, onde
 * é testável.
 *
 * ## Dado que falta
 *
 * Número que o pipeline não mediu sai `null` e vira "Dado não apurado" na
 * página — nunca zero. Zero é afirmação ("ninguém clicou"); ausência de medição
 * não é. Os campos novos do api-admin (reações, pico, top 5, carrinho da live)
 * são opcionais justamente para o painel funcionar antes daquele deploy.
 *
 * ## Seções e blocos
 *
 * A pessoa escolhe as seções (e alguns blocos dentro delas) antes de baixar.
 * Seção sem dado nesta execução fica indisponível com o motivo, em vez de
 * imprimir um bloco vazio.
 */
import {
  LIVE_ATTRIBUTION_WINDOW_LABELS,
  LiveAttributionByLive,
  LiveAttributionWindow,
  LiveCartItem,
  LiveRevenueLayerProduct,
} from '../../types'

// ─── Seções e blocos ─────────────────────────────────────────────────────────

export type LiveReportSectionId = 'hero' | 'audience' | 'engagement' | 'script' | 'sales' | 'opportunities' | 'insights'

export const LIVE_REPORT_SECTION_ORDER: LiveReportSectionId[] = [
  'hero', 'audience', 'engagement', 'script', 'sales', 'opportunities', 'insights',
]

/** Nome da seção na lista de seleção (não é o título impresso). */
export const LIVE_REPORT_SECTION_TITLES: Record<LiveReportSectionId, string> = {
  hero: 'Capa e resumo',
  audience: 'Audiência & retenção',
  engagement: 'Engajamento & origem',
  script: 'Roteiro da live',
  sales: 'Resultados de vendas',
  opportunities: 'Oportunidades não capturadas',
  insights: 'Insights & próximos passos',
}

export type LiveReportBlockId =
  | 'audience.rotation'
  | 'audience.topViewers'
  | 'engagement.origin'
  | 'script.timeline'
  | 'script.topProducts'
  | 'sales.funnel'
  | 'sales.cartKpis'
  | 'sales.cartDetail'
  | 'opportunities.gap'

export const LIVE_REPORT_BLOCKS: Array<{ id: LiveReportBlockId; section: LiveReportSectionId; label: string }> = [
  { id: 'audience.rotation', section: 'audience', label: 'Rotação de audiência' },
  { id: 'audience.topViewers', section: 'audience', label: 'Top 5 mais engajados' },
  { id: 'engagement.origin', section: 'engagement', label: 'Origem por canal' },
  { id: 'script.timeline', section: 'script', label: 'Linha do tempo de produtos' },
  { id: 'script.topProducts', section: 'script', label: 'Top produtos por cliques' },
  { id: 'sales.funnel', section: 'sales', label: 'Funil de vendas' },
  { id: 'sales.cartKpis', section: 'sales', label: 'Indicadores de carrinho' },
  { id: 'sales.cartDetail', section: 'sales', label: 'Produtos no carrinho' },
  { id: 'opportunities.gap', section: 'opportunities', label: 'Maior gap entre clique e compra' },
]

export interface Availability {
  available: boolean
  /** Por que não pode ser incluído — vai para a lista de seleção. */
  reason?: string
}

export interface LiveReportSelection {
  sections: Record<LiveReportSectionId, boolean>
  blocks: Record<LiveReportBlockId, boolean>
}

export function defaultSelection(model: Pick<LiveReportModel, 'availability' | 'blockAvailability'>): LiveReportSelection {
  const sections = {} as Record<LiveReportSectionId, boolean>
  for (const id of LIVE_REPORT_SECTION_ORDER) sections[id] = model.availability[id].available
  const blocks = {} as Record<LiveReportBlockId, boolean>
  for (const b of LIVE_REPORT_BLOCKS) blocks[b.id] = model.blockAvailability[b.id].available
  return { sections, blocks }
}

/** Seções efetivamente impressas, na ordem da página. */
export function includedSections(selection: LiveReportSelection, model: Pick<LiveReportModel, 'availability'>): LiveReportSectionId[] {
  return LIVE_REPORT_SECTION_ORDER.filter((id) => selection.sections[id] && model.availability[id].available)
}

/** Bloco impresso: marcado, com dado, e dentro de uma seção impressa. */
export function blockIncluded(
  id: LiveReportBlockId,
  selection: LiveReportSelection,
  model: Pick<LiveReportModel, 'availability' | 'blockAvailability'>,
): boolean {
  const section = LIVE_REPORT_BLOCKS.find((b) => b.id === id)!.section
  return selection.sections[section] && model.availability[section].available && selection.blocks[id] && model.blockAvailability[id].available
}

// ─── Formatação ──────────────────────────────────────────────────────────────

const TZ = 'America/Sao_Paulo'
export const NO_DATA = 'Dado não apurado'

function partsInTz(iso: string | null | undefined): { day: number; month: number; year: number; hour: number; minute: number } | null {
  const ms = Date.parse(iso || '')
  if (Number.isNaN(ms)) return null
  const parts = new Intl.DateTimeFormat('pt-BR', {
    timeZone: TZ, day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(new Date(ms))
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value ?? NaN)
  return { day: get('day'), month: get('month'), year: get('year'), hour: get('hour') % 24, minute: get('minute') }
}

/** "22/10/2026" */
export function formatDate(iso: string | null | undefined): string {
  const p = partsInTz(iso)
  if (!p) return '—'
  return `${String(p.day).padStart(2, '0')}/${String(p.month).padStart(2, '0')}/${p.year}`
}

/** "15h" na hora cheia, "18h01" fora dela. */
export function formatClock(iso: string | null | undefined): string {
  const p = partsInTz(iso)
  if (!p) return '—'
  return p.minute === 0 ? `${p.hour}h` : `${p.hour}h${String(p.minute).padStart(2, '0')}`
}

/**
 * Duração da transmissão: "3h01", "45 min". Trunca os dois extremos ao minuto
 * antes de subtrair, senão a duração arredonda para cima enquanto os relógios
 * ao lado, truncados, mostram um minuto a menos — o par tem que fechar.
 */
export function formatLiveDuration(startIso: string, endIso: string | null): string | null {
  if (!endIso) return null
  const mins = Math.floor(Date.parse(endIso) / 60000) - Math.floor(Date.parse(startIso) / 60000)
  if (Number.isNaN(mins) || mins <= 0) return null
  return mins < 60 ? `${mins} min` : `${Math.floor(mins / 60)}h${String(mins % 60).padStart(2, '0')}`
}

/** "1.360" */
export function formatInt(n: number): string {
  return Math.round(n || 0).toLocaleString('pt-BR')
}

/** "R$ 17.593" — inteiro, com espaço (o formato do template). */
export function formatMoney(v: number): string {
  return `R$ ${Math.round(v || 0).toLocaleString('pt-BR')}`
}

/**
 * Tempo assistido: "46s", "32min 45s", "2h26". Mesma régua nos cards e no top 5.
 */
export function formatWatch(sec: number): string {
  const s = Math.max(0, Math.round(sec || 0))
  if (s < 60) return `${s}s`
  if (s < 3600) {
    const m = Math.floor(s / 60)
    const rest = s % 60
    return rest === 0 ? `${m}min` : `${m}min ${rest}s`
  }
  return `${Math.floor(s / 3600)}h${String(Math.floor((s % 3600) / 60)).padStart(2, '0')}`
}

/** Tempo TOTAL assistido: a partir de 10h só a hora cheia ("33h"), que é o que se lê num card. */
export function formatWatchTotal(sec: number): string {
  return sec >= 10 * 3600 ? `${Math.round(sec / 3600)}h` : formatWatch(sec)
}

function pctInt(part: number, total: number): number {
  return total > 0 ? Math.round((part / total) * 100) : 0
}

function plural(n: number, one: string, many: string): string {
  return n === 1 ? one : many
}

// ─── Origem ──────────────────────────────────────────────────────────────────

/**
 * Traduz `utm_source` / `referral:<domínio>` / `direto` no canal do relatório.
 * Domínios conhecidos colapsam na rede (l.instagram.com e instagram.com são a
 * mesma origem para quem lê). O que não é conhecido sai como o domínio.
 */
export function channelLabel(source: string): string {
  const s = (source || '').trim().toLowerCase()
  if (!s || s === 'direto' || s === 'direct' || s === '(direct)') return 'Direto'
  const host = s.startsWith('referral:') ? s.slice('referral:'.length) : s
  if (/instagram|(^|\.)ig(\.|$)/.test(host)) return 'Instagram'
  if (/facebook|fb\.com|fb$/.test(host)) return 'Facebook'
  if (/google/.test(host)) return 'Google'
  if (/linktr\.ee|linktree/.test(host)) return 'Linktree'
  if (/whatsapp|wa\.me/.test(host)) return 'WhatsApp'
  if (/tiktok/.test(host)) return 'TikTok'
  if (/youtube|youtu\.be/.test(host)) return 'YouTube'
  if (/^(email|e-mail|mail|newsletter|crm)$/.test(host)) return 'E-mail'
  if (s.startsWith('referral:')) return host.replace(/^www\./, '')
  return source.charAt(0).toUpperCase() + source.slice(1)
}

/** Cor fixa por canal (o leitor reconhece a mesma cor entre lives); o resto cicla. */
const CHANNEL_COLORS: Record<string, string> = {
  Google: '#F5A324',
  Direto: '#4F5FE0',
  Instagram: '#17A6B4',
  Facebook: '#E1306C',
  Outros: '#D6D6D6',
}
const CHANNEL_FALLBACK = ['#7C5CFA', '#22A06B', '#0EA5E9', '#EF6C3E', '#A855F7']

/** Canais exibidos antes de juntar o resto em "Outros". */
const MAX_CHANNELS = 5

// ─── Modelo ──────────────────────────────────────────────────────────────────

export interface LiveReportKpi {
  /** `null` = não medido nesta execução → "Dado não apurado". */
  value: string | null
  label: string
  tip: string
  /** Tem nota em "Notas sobre os dados" (sai com asterisco). */
  noted?: boolean
}

export interface LiveReportModel {
  companyTitle: string
  liveTitle: string
  /** Para o nome do arquivo. */
  fileDate: string
  availability: Record<LiveReportSectionId, Availability>
  blockAvailability: Record<LiveReportBlockId, Availability>
  hero: {
    title: string
    company: string
    meta: string[]
    /** O primeiro é o destaque azul (receita). */
    kpis: LiveReportKpi[]
  }
  audience: {
    kpis: LiveReportKpi[]
    bands: Array<{ label: string; valueText: string; pct: number; alpha: number }>
    topViewers: Array<{ rank: number; name: string; detail: string }>
  }
  engagement: {
    kpis: LiveReportKpi[]
    channels: Array<{ label: string; viewers: number; pct: number; color: string }>
  }
  script: {
    timeline: Array<{ timeText: string; name: string; filled: boolean }>
    timelineNote: string
    topProducts: Array<{ name: string; clicks: number }>
  }
  sales: {
    kpis: LiveReportKpi[]
    /** Atribuída direta e influenciada — só com o backend que as calcula. */
    revenueBreakdown: LiveReportKpi[]
    /**
     * Ressalvas da análise em linguagem de cliente (o relatório vai para a
     * loja). Saem no fim da seção de vendas; KPI afetado leva asterisco.
     */
    dataNotes: string[]
    funnel: Array<{ label: string; valueText: string; widthPct: number; alpha: number }>
    cartKpis: LiveReportKpi[]
    cartProducts: Array<{ name: string; units: number; users: number; valueText: string }>
    /** Produtos além do teto da tabela. */
    cartProductsHidden: number
  }
  opportunities: {
    cards: LiveReportKpi[]
    note: string | null
    gap: Array<{ name: string; clicks: number; cart: number | null; sold: number | null }>
  }
  insights: { main: string; others: string[] }
  footer: { left: string; right: string }
}

/** Linhas da tabela de carrinho antes de resumir o resto numa nota. */
const CART_PRODUCTS_LIMIT = 40
/** Produtos na linha do tempo e com bolinha cheia. */
const TIMELINE_LIMIT = 8
const TIMELINE_FILLED = 5
const TOP_PRODUCTS_LIMIT = 5

const keyOf = (id: string | null | undefined, name: string | null | undefined) =>
  (id && id.trim()) || `name:${(name || '').trim().toLowerCase()}`

export function buildLiveReportModel(input: {
  live: LiveAttributionByLive
  companyTitle: string
  window: LiveAttributionWindow
}): LiveReportModel {
  const { live, companyTitle, window } = input
  const report = live.audienceReport
  const layers = live.revenueLayers
  const cart = live.cartAudience
  const windowLabel = LIVE_ATTRIBUTION_WINDOW_LABELS[window]
  const windowLower = windowLabel.toLowerCase()

  const noReport = 'relatório de audiência indisponível nesta execução'

  // ── Números de base ───────────────────────────────────────────────────
  // Receita ATRIBUÍDA = produtos do catálogo da live comprados por quem
  // assistiu (live-metrics.ts). Não é o total das camadas: a camada
  // "influenciados" leva itens fora do catálogo, que são a receita
  // INFLUENCIADA e saem separados abaixo.
  const viewers = report?.audience.uniqueViewers ?? null
  const attributed = live.metrics?.attributed ?? { orders: live.liveInfluencedMatched ?? 0, revenue: live.liveInfluencedRevenue ?? 0 }
  const revenue = attributed.revenue
  const orders = attributed.orders
  const ticket = orders > 0 ? revenue / orders : null
  const funnel = report?.funnel
  const caveats = live.caveats ?? []
  const cartCaveats = caveats.filter((c) => c.affects.includes('liveCart'))
  // Carrinho nativo sem base (sem GA4, ou GA4 que não sustenta): o valor do
  // carrinho fica restrito ao que entrou pelo carrinho da Widde.
  const cartWiddeOnly = cartCaveats.some((c) => c.severity === 'critical' || c.code === 'shopify-analytics' || c.code === 'no-ga4')

  // ── Capa ──────────────────────────────────────────────────────────────
  const liveTitle = live.liveTitle || report?.live.title || ''
  const startIso = live.liveStartedAt
  const endIso = live.liveEndedAt
  const duration = formatLiveDuration(startIso, endIso)
  const meta = [
    endIso
      ? `${formatDate(startIso)} · ${formatClock(startIso)} às ${formatClock(endIso)} (Brasília)`
      : `${formatDate(startIso)} · desde ${formatClock(startIso)} (Brasília) · em curso`,
    ...(duration ? [`Duração ${duration}`] : []),
    `Janela de atribuição ${windowLower}`,
  ]
  const revenueKpi: LiveReportKpi = {
    value: formatMoney(revenue),
    label: 'Receita atribuída',
    tip: `Valor dos produtos apresentados na live comprados por quem assistiu, dentro da janela de atribuição de ${windowLower}.`,
  }
  const ordersKpi: LiveReportKpi = {
    value: formatInt(orders),
    label: 'Pedidos atribuídos',
    tip: 'Pedidos de quem assistiu à live com pelo menos um produto apresentado nela, dentro da janela de atribuição.',
  }
  const ticketKpi: LiveReportKpi = {
    value: ticket === null ? null : formatMoney(ticket),
    label: 'Ticket médio',
    tip: 'Valor médio de cada pedido atribuído à live.',
  }
  const viewersKpi: LiveReportKpi = {
    value: viewers === null ? null : formatInt(viewers),
    label: 'Espectadores únicos',
    tip: 'Número de pessoas diferentes que assistiram à live.',
  }
  const hero = {
    title: liveTitle || 'Resultados da live',
    company: companyTitle,
    meta,
    kpis: [revenueKpi, ordersKpi, viewersKpi, ticketKpi],
  }

  // ── Audiência ─────────────────────────────────────────────────────────
  const watch = report?.audience.watchTime
  const peak = report?.audience.peakConcurrent.value ?? null
  const audienceKpis: LiveReportKpi[] = [
    { ...viewersKpi, tip: 'Pessoas diferentes que assistiram à live.' },
    { value: watch ? formatWatchTotal(watch.totalSec) : null, label: 'Tempo total assistido', tip: 'Soma do tempo que todos os espectadores passaram assistindo à live.' },
    { value: watch ? formatWatch(watch.avgSec) : null, label: 'Média por espectador', tip: 'Tempo médio que cada pessoa ficou assistindo.' },
    { value: watch ? formatWatch(watch.maxSec) : null, label: 'Tempo mais longo assistido', tip: 'O maior tempo que um único espectador ficou assistindo.' },
    { value: peak === null ? null : formatInt(peak), label: 'Pico de audiência', tip: 'Maior número de pessoas assistindo ao mesmo tempo.' },
    { value: report ? formatInt(report.audience.views) : null, label: 'Vezes que o vídeo foi aberto', tip: 'Quantas vezes o vídeo foi aberto, incluindo reaberturas do mesmo espectador.' },
  ]

  // Faixas: as duas últimas do api-admin (30–60 min, + 1 hora) viram "30min+",
  // como no template — com poucos espectadores ali, separar só polui.
  const rawBands = report?.audience.watchTimeBands ?? []
  const merged = rawBands.length > 5
    ? [...rawBands.slice(0, 4), {
        label: '30min+',
        viewers: rawBands.slice(4).reduce((s, b) => s + b.viewers, 0),
        pct: rawBands.slice(4).reduce((s, b) => s + b.pct, 0),
      }]
    : rawBands
  const bands = merged.map((b, i) => ({
    label: b.label.replace(/^< 1 min$/, '<1 min'),
    valueText: `${formatInt(b.viewers)} · ${Math.round(b.pct)}%`,
    pct: Math.max(0, Math.min(100, b.pct)),
    // Do mais claro (pouco tempo) ao azul cheio (mais tempo), como no template.
    alpha: i === merged.length - 1 ? 1 : Math.round((0.3 + (0.45 * i) / Math.max(1, merged.length - 1)) * 100) / 100,
  }))

  const topViewers = (report?.audience.topViewers ?? []).map((v) => {
    const parts = [`${formatWatch(v.watchSec)} assistido`]
    if (v.messages > 0) parts.push(`${v.messages} ${plural(v.messages, 'msg', 'msgs')}`)
    if (v.clicks > 0) parts.push(`${v.clicks} ${plural(v.clicks, 'clique', 'cliques')}`)
    return { rank: v.rank, name: `Espectador #${v.rank}`, detail: parts.join(' · ') }
  })

  // ── Engajamento & origem ──────────────────────────────────────────────
  const reactions = report?.engagement.reactions ?? null
  const engagementKpis: LiveReportKpi[] = [
    { value: report ? formatInt(report.engagement.chat.messages) : null, label: 'Mensagens no chat', tip: 'Quantidade de mensagens enviadas no chat durante a live.' },
    { value: reactions ? formatInt(reactions.likes) : null, label: 'Likes', tip: 'Curtidas recebidas durante a transmissão.' },
    { value: reactions ? formatInt(reactions.shares) : null, label: 'Compartilhamentos', tip: 'Quantidade de vezes que a live foi compartilhada.' },
    { value: report ? formatInt(report.engagement.leads) : null, label: 'Leads capturados', tip: 'Contatos coletados durante a live (cupom, formulário).' },
  ]

  const byChannel = new Map<string, number>()
  for (const t of report?.audience.traffic ?? []) {
    const label = channelLabel(t.source)
    byChannel.set(label, (byChannel.get(label) ?? 0) + t.viewers)
  }
  const sortedChannels = [...byChannel.entries()].filter(([, n]) => n > 0).sort((a, b) => b[1] - a[1])
  const channelRows = sortedChannels.length > MAX_CHANNELS
    ? [...sortedChannels.slice(0, MAX_CHANNELS - 1), ['Outros', sortedChannels.slice(MAX_CHANNELS - 1).reduce((s, [, n]) => s + n, 0)] as [string, number]]
    : sortedChannels
  const channelTotal = channelRows.reduce((s, [, n]) => s + n, 0)
  let fallbackColor = 0
  const channels = channelRows.map(([label, n]) => ({
    label,
    viewers: n,
    pct: pctInt(n, channelTotal),
    color: CHANNEL_COLORS[label] ?? CHANNEL_FALLBACK[fallbackColor++ % CHANNEL_FALLBACK.length],
  }))

  // ── Roteiro ───────────────────────────────────────────────────────────
  const clicked = report?.products.clicksByProduct ?? []
  const byClicks = [...clicked].sort((a, b) => b.clicks - a.clicks || b.clickers - a.clickers)
  const filledKeys = new Set(byClicks.slice(0, TIMELINE_FILLED).map((p) => keyOf(p.productId, p.title)))
  const hasTimes = byClicks.some((p) => p.firstClickAt)
  const timelineSource = byClicks.slice(0, TIMELINE_LIMIT)
  // Com horário: ordem do primeiro clique (quando o produto apareceu). Sem
  // horário (api-admin anterior): a ordem por cliques, como no template.
  const timelineOrdered = hasTimes
    ? [...timelineSource].sort((a, b) => (Date.parse(a.firstClickAt || '') || Infinity) - (Date.parse(b.firstClickAt || '') || Infinity))
    : timelineSource
  const timeline = timelineOrdered.map((p) => ({
    timeText: p.firstClickAt ? formatClock(p.firstClickAt) : '–',
    name: p.title || p.productId,
    filled: filledKeys.has(keyOf(p.productId, p.title)),
  }))
  const timelineNote = hasTimes
    ? `Horário = primeiro clique no produto durante a live. Bolinha cheia = ${Math.min(TIMELINE_FILLED, byClicks.length)} produtos com mais cliques.`
    : 'Bolinha cheia = produto com mais cliques. Timeline baseada no total de cliques por produto.'
  const topProducts = byClicks.slice(0, TOP_PRODUCTS_LIMIT).map((p) => ({ name: p.title || p.productId, clicks: p.clicks }))

  // ── Vendas ────────────────────────────────────────────────────────────
  const salesKpis: LiveReportKpi[] = [
    ordersKpi,
    { ...revenueKpi, tip: 'Valor dos produtos apresentados na live comprados por quem assistiu.' },
    ticketKpi,
  ]
  const directMetric = live.metrics?.attributedDirect
  const influencedMetric = live.metrics?.influenced
  const revenueBreakdown: LiveReportKpi[] = live.metrics
    ? [
        {
          value: directMetric ? formatMoney(directMetric.revenue) : null,
          label: 'Receita atribuída direta',
          tip: 'Parte da receita atribuída em que o espectador adicionou o produto ao carrinho da Widde durante a live.',
          noted: !directMetric,
        },
        {
          value: influencedMetric ? formatMoney(influencedMetric.revenue) : null,
          label: 'Receita influenciada',
          tip: 'Compras de quem assistiu à live, na mesma janela, de produtos que NÃO foram apresentados nela.',
        },
      ]
    : []

  // Ressalvas em linguagem de cliente — uma por código, na ordem de gravidade.
  const NOTE_BY_CODE: Record<string, string> = {
    'shopify-analytics': 'Carrinho: considera só as adições feitas pelo carrinho da Widde. A loja mede o site pelo Shopify Analytics, sem Google Analytics, e o carrinho nativo não é visível para nós.',
    'no-ga4': 'Carrinho: considera só as adições feitas pelo carrinho da Widde. A loja não tem Google Analytics conectado, e o carrinho nativo não é visível para nós.',
    'ga4-add-to-cart-missing': 'Carrinho: o Google Analytics da loja não registra adições ao carrinho com os produtos, então os valores consideram só o carrinho da Widde.',
    'ga4-add-to-cart-suspect': 'Carrinho: os dados de carrinho que o Google Analytics da loja envia estão incompletos, então os valores consideram só o carrinho da Widde.',
    'ga4-cart-truncated': 'Carrinho: o Google Analytics agrupou parte das adições por volume; os valores de carrinho são um piso.',
    'cart-time-approximate': 'Carrinho: o Google Analytics da loja informa o horário das adições de forma aproximada; adições sem horário confirmado ficaram fora do carrinho da live.',
    'attributed-direct-unavailable': 'Receita atribuída direta: não foi possível apurar nesta análise.',
  }
  const dataNotes = [...new Set(caveats.map((c) => NOTE_BY_CODE[c.code]).filter((n): n is string => !!n))]

  const funnelSteps: Array<{ label: string; n: number; unit: [string, string] }> = viewers === null || !funnel
    ? []
    : [
        { label: 'Espectadores únicos', n: viewers, unit: ['espectador', 'espectadores'] },
        { label: 'Cliques em produtos', n: funnel.clicks, unit: ['clique', 'cliques'] },
        { label: 'Adições ao carrinho', n: funnel.cartAdds, unit: ['adição', 'adições'] },
        { label: 'Checkouts iniciados', n: funnel.checkoutStarters, unit: ['checkout', 'checkouts'] },
        { label: 'Vendas confirmadas', n: orders, unit: ['venda', 'vendas'] },
      ]
  const funnelTop = funnelSteps[0]?.n ?? 0
  const funnelView = funnelSteps.map((s, i) => {
    const ratio = funnelTop > 0 ? Math.min(1, s.n / funnelTop) : 0
    return {
      label: s.label,
      valueText: `${formatInt(s.n)} ${plural(s.n, s.unit[0], s.unit[1])}`,
      // Piso de 5% para o rótulo caber; o topo é sempre a barra cheia.
      widthPct: i === 0 ? 100 : Math.max(5, Math.round(ratio * 100)),
      alpha: i === 0 ? 1 : Math.round((0.3 + 0.7 * ratio) * 100) / 100,
    }
  })

  // Carrinho da live: todos os carrinhos (Widde + nativo da loja via GA4),
  // quando a fonte sustenta; senão só o da Widde. Backend antigo: o resumo
  // do widget (só Widde) que o api-admin já mandava.
  const summaryCart = cart?.summary.liveCart ?? null
  const widdeSummaryCart = report?.liveCart ?? null
  const cartValue = summaryCart
    ? cartWiddeOnly
      ? summaryCart.byOrigin.widde.value + summaryCart.byOrigin.both.value
      : summaryCart.value
    : widdeSummaryCart
      ? widdeSummaryCart.valueCents / 100
      : null
  const cartValueKpi: LiveReportKpi = summaryCart && !cartWiddeOnly
    ? {
        value: cartValue === null ? null : formatMoney(cartValue),
        label: 'Valor no carrinho (durante a live)',
        tip: 'Soma do valor dos itens que quem assistiu adicionou ao carrinho durante a live — no carrinho da Widde e no carrinho da loja —, comprados ou não.',
        noted: cartCaveats.length > 0,
      }
    : {
        value: cartValue === null ? null : formatMoney(cartValue),
        label: 'Valor no carrinho Widde (durante a live)',
        tip: 'Soma do valor dos itens adicionados ao carrinho da Widde durante a transmissão ao vivo.',
        noted: cartCaveats.length > 0,
      }
  const cartKpis: LiveReportKpi[] = [
    { value: funnel ? formatInt(funnel.cartAdds) : null, label: 'Unidades no carrinho', tip: 'Total de unidades adicionadas ao carrinho da Widde durante a live.' },
    { value: funnel ? formatInt(funnel.cartAdders) : null, label: 'Usuários que adicionaram', tip: 'Quantas pessoas colocaram ao menos um produto no carrinho da Widde.' },
    { value: funnel ? formatInt(funnel.checkoutStarters) : null, label: 'Checkouts iniciados', tip: 'Quantas pessoas iniciaram o processo de pagamento.' },
    cartValueKpi,
  ]

  // Produtos que o público pôs no carrinho DURANTE a live.
  const cartByProduct = new Map<string, { name: string; units: number; users: Set<string>; value: number }>()
  for (const viewer of cart?.viewers ?? []) {
    for (const item of viewer.items as LiveCartItem[]) {
      if (item.phase !== 'during-live' || item.addedQuantity <= 0) continue
      // Mesma régua do valor no carrinho: sem base no carrinho nativo, só Widde.
      if (cartWiddeOnly && item.origin === 'ga4') continue
      const k = keyOf(item.productId, item.productTitle)
      const row = cartByProduct.get(k) ?? { name: item.productTitle || item.productId, units: 0, users: new Set<string>(), value: 0 }
      row.units += item.addedQuantity
      row.users.add(viewer.widdeUid)
      row.value += (item.unitPrice || 0) * item.addedQuantity
      cartByProduct.set(k, row)
    }
  }
  const cartRows = [...cartByProduct.values()].sort((a, b) => b.units - a.units || b.value - a.value)
  const cartProducts = cartRows.slice(0, CART_PRODUCTS_LIMIT).map((r) => ({
    name: r.name, units: r.units, users: r.users.size, valueText: formatMoney(r.value),
  }))

  // ── Oportunidades ─────────────────────────────────────────────────────
  // "Em aberto" = abandonado (janela fechada, sem compra) + o que ainda está no
  // carrinho com a janela aberta. Com contato = dá para acionar agora.
  const openViewers = (cart?.viewers ?? []).filter((v) => v.items.some((i) => i.abandoned || i.stillInCart))
  const withContact = openViewers.filter((v) => v.identified || !!v.leadContact).length
  const openValue = cart ? cart.summary.abandoned.value + cart.summary.pending.value : null
  const opportunityCards: LiveReportKpi[] = [
    { value: cart ? formatInt(withContact) : null, label: 'Carrinhos abandonados com contato', tip: 'Pessoas identificadas que deixaram produto no carrinho sem finalizar a compra.' },
    { value: openValue === null ? null : formatMoney(openValue), label: 'Valor em aberto', tip: 'Soma do valor dos carrinhos abandonados ainda não recuperado.' },
  ]
  const opportunityNote = cart && !cart.summary.windowClosed
    ? 'A janela de atribuição ainda está aberta: parte desse valor ainda pode virar venda.'
    : cart?.summary.cartValueIsPartial
      ? 'Parte dos itens não tem preço conhecido: o valor em aberto pode estar abaixo do real.'
      : cartCaveats.length > 0
        ? 'Valores de carrinho com ressalva — veja as notas sobre os dados em Resultados de vendas.'
        : null

  const soldByProduct = new Map<string, number>()
  const allLayerProducts: LiveRevenueLayerProduct[] = layers
    ? [...layers.layers['full-funnel'].products, ...layers.layers['click-direct'].products, ...layers.layers.influenced.products]
    : []
  // Indexado por id E por nome: o clique traz o id externo, mas nem toda linha
  // das camadas tem id (influenciados vêm só com o nome do item do pedido).
  const addSold = (k: string, n: number) => soldByProduct.set(k, (soldByProduct.get(k) ?? 0) + n)
  for (const p of allLayerProducts) {
    if (p.productId?.trim()) addSold(keyOf(p.productId, null), p.unitsSold)
    if (p.name?.trim()) addSold(keyOf(null, p.name), p.unitsSold)
  }
  const cartUnitsFor = (id: string, name: string) => {
    const hit = cartByProduct.get(keyOf(id, name)) ?? cartByProduct.get(keyOf(null, name))
    return hit?.units ?? 0
  }
  const gap = byClicks.slice(0, TOP_PRODUCTS_LIMIT).map((p) => ({
    name: p.title || p.productId,
    clicks: p.clicks,
    cart: cart ? cartUnitsFor(p.productId, p.title) : null,
    sold: layers
      ? (p.productId?.trim() ? soldByProduct.get(keyOf(p.productId, null)) : undefined) ?? soldByProduct.get(keyOf(null, p.title)) ?? 0
      : null,
  }))

  // ── Insights ──────────────────────────────────────────────────────────
  const candidates: string[] = []
  if (withContact > 0) {
    candidates.push(
      `Recuperar ${withContact === 1 ? 'o usuário que deixou' : `os ${formatInt(withContact)} usuários que deixaram`} produto no carrinho` +
        (openValue && openValue > 0 ? ` (${formatMoney(openValue)} em aberto)` : '') +
        ' com disparo de WhatsApp em até 2h após o fim da live.',
    )
  } else if (funnel && funnel.cartAdders > orders) {
    candidates.push(
      `Recuperar os ${formatInt(funnel.cartAdders)} usuários que adicionaram ao carrinho com disparo de WhatsApp em até 2h após o fim da live.`,
    )
  }
  const under1 = Math.round(rawBands[0]?.pct ?? 0)
  if (under1 >= 40) {
    candidates.push(`${under1}% saem no 1º minuto: abrir a transmissão já com o produto e a oferta na tela, sem esperar o 2º ou 3º minuto para mostrar valor.`)
  }
  const stalled = gap.find((g) => g.sold === 0 && g.clicks >= 10)
  if (stalled) {
    candidates.push(`${stalled.name} teve ${formatInt(stalled.clicks)} cliques e nenhuma venda: revisar preço, grade disponível e a oferta mostrada na live.`)
  }
  if (report && report.engagement.leads > 0) {
    candidates.push(`${formatInt(report.engagement.leads)} ${plural(report.engagement.leads, 'lead capturado', 'leads capturados')}: incluir na régua de remarketing e no convite da próxima live.`)
  }
  if (channels[0] && channels[0].label !== 'Outros' && channels[0].pct >= 40) {
    candidates.push(`${channels[0].label} trouxe ${channels[0].pct}% do público: reforçar a divulgação nesse canal antes da próxima live.`)
  }
  const insights = {
    main: candidates[0] ?? 'Sem ponto de atenção relevante nesta live: manter o formato e comparar com a próxima transmissão.',
    others: candidates.slice(1, 3),
  }

  // ── Disponibilidade ───────────────────────────────────────────────────
  const availability: Record<LiveReportSectionId, Availability> = {
    hero: { available: true },
    audience: report ? { available: true } : { available: false, reason: noReport },
    engagement: report ? { available: true } : { available: false, reason: noReport },
    script: clicked.length > 0 ? { available: true } : { available: false, reason: report ? 'nenhum clique em produto nesta live' : noReport },
    sales: { available: true },
    opportunities: cart ? { available: true } : { available: false, reason: 'carrinho do público indisponível nesta execução' },
    insights: { available: true },
  }
  const blockAvailability: Record<LiveReportBlockId, Availability> = {
    'audience.rotation': bands.length > 0 ? { available: true } : { available: false, reason: 'nenhuma sessão com tempo medido' },
    'audience.topViewers': topViewers.length > 0
      ? { available: true }
      : {
          available: false,
          // Lista vazia: ninguém com sessão medida OU a leitura falhou no
          // api-admin (que devolve vazio em vez de derrubar o relatório).
          reason: report?.audience.topViewers ? 'sem dado de engajamento por espectador nesta execução' : 'api-admin ainda não envia este dado',
        },
    'engagement.origin': channels.length > 0 ? { available: true } : { available: false, reason: 'sem origem de tráfego registrada' },
    'script.timeline': timeline.length > 0 ? { available: true } : { available: false, reason: 'nenhum clique em produto' },
    'script.topProducts': topProducts.length > 0 ? { available: true } : { available: false, reason: 'nenhum clique em produto' },
    'sales.funnel': funnelView.length > 0 ? { available: true } : { available: false, reason: noReport },
    'sales.cartKpis': funnel ? { available: true } : { available: false, reason: noReport },
    'sales.cartDetail': cartProducts.length > 0
      ? { available: true }
      : { available: false, reason: cart ? 'nenhum produto adicionado durante a live' : 'carrinho do público indisponível' },
    'opportunities.gap': gap.length > 0 ? { available: true } : { available: false, reason: 'nenhum clique em produto' },
  }

  return {
    companyTitle,
    liveTitle,
    fileDate: formatDate(startIso).split('/').reverse().join('-'),
    availability,
    blockAvailability,
    hero,
    audience: { kpis: audienceKpis, bands, topViewers },
    engagement: { kpis: engagementKpis, channels },
    script: { timeline, timelineNote, topProducts },
    sales: {
      kpis: salesKpis,
      revenueBreakdown,
      dataNotes,
      funnel: funnelView,
      cartKpis,
      cartProducts,
      cartProductsHidden: Math.max(0, cartRows.length - CART_PRODUCTS_LIMIT),
    },
    opportunities: { cards: opportunityCards, note: opportunityNote, gap },
    insights,
    footer: { left: 'Gerado automaticamente · Widde', right: [companyTitle, liveTitle].filter(Boolean).join(' · ') },
  }
}
